import { formatScenes } from 'containers/world/utils';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { goToScene, setTourError, updateWorldParams } from 'store/actions';

export const useWorldActions = () => {
  const { worldParams, json, currentScene } = useSelector((state) => state);
  const dispatch = useDispatch();
  const [currentPano, setCurrentPano] = useState(null);

  const { groupId, sceneId, hotspotId } = worldParams;

  const loadSceneById = useCallback(
    ({ groupId, sceneId }) => {
      const { scenes, media } = json;

      const newScenes = formatScenes(media, scenes);
      const scene =
        newScenes.find((p) => p.id === sceneId && p.groupId === groupId) ||
        null;
      if (scene) {
        return setCurrentPano(scene);
      }
      return dispatch(setTourError({ code: 'NO_SCENE', detail: sceneId }));
    },
    [dispatch, json]
  );

  const selectHotspot = useCallback(
    ({ selectedHotspotId, showHotspot }) => {
      if (!currentPano) return null;

      const { media, featuredHotspot = [] } = json;
      const foundFeature = featuredHotspot.find(
        (h) => h.id === selectedHotspotId
      );

      if (foundFeature) {
        if (selectedHotspotId === hotspotId) {
          return showHotspot?.(foundFeature);
        }

        return dispatch(
          updateWorldParams({
            groupId,
            sceneId,
            hotspotId: selectedHotspotId,
          })
        );
      }

      const currentMedia = media.find((m) => m.id === selectedHotspotId);
      const hotspot = {
        id: currentMedia.friendlyName,
        _id: currentMedia._id,
        media: currentMedia,
      };

      if (!hotspot && sceneId) {
        return dispatch(
          updateWorldParams({ groupId, sceneId, hotspotId: null })
        );
      }

      if (selectedHotspotId !== hotspotId) {
        return dispatch(
          updateWorldParams({
            groupId,
            sceneId,
            hotspotId: selectedHotspotId,
          })
        );
      }
    },
    [currentPano, dispatch, groupId, hotspotId, json, sceneId]
  );

  const selectSceneById = useCallback(
    (selectedGroupId, selectedSceneId) => {
      if (!selectedGroupId) {
        return dispatch(
          setTourError({ code: 'NO_GROUP', detail: selectedGroupId })
        );
      }

      const { groups: groupList } = json;
      const isGroupExisted = groupList.some((g) => g.id === selectedGroupId);
      if (!isGroupExisted) {
        return dispatch(
          setTourError({ code: 'NO_GROUP', detail: selectedGroupId })
        );
      }

      if (selectedGroupId === groupId && selectedSceneId === sceneId) {
        if (
          currentScene.id === selectedSceneId &&
          currentScene.groupId === selectedGroupId
        ) {
          return loadSceneById({
            groupId: selectedGroupId,
            sceneId: selectedSceneId,
          });
        }
        return dispatch(goToScene(selectedGroupId, selectedSceneId));
      }

      return dispatch(
        updateWorldParams({
          groupId: selectedGroupId,
          sceneId: selectedSceneId,
        })
      );
    },
    [
      json,
      groupId,
      sceneId,
      dispatch,
      currentScene.id,
      currentScene.groupId,
      loadSceneById,
    ]
  );

  return {
    selectSceneById,
    selectHotspot,
    currentPano,
    setCurrentPano,
  };
};
